import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
import { getPrivateFileUrl } from '@/shared/lib/utils/privateFiles';
import type { ChatApiError } from '../api/client';
import type { ChatApi } from '../api/chatApi';
import type { ChatRuntime } from '../api/runtime';
import {
  mapChatDetail,
  mapChatUser,
  mapMessage,
  mapPinnedMessage,
  mapThread,
  resolvePersonalChatId,
} from '../api/mappers';
import {
  CHAT_GROUP_MAX_PARTICIPANTS,
  CHAT_LIST_PAGE_SIZE,
  CHAT_LIST_SEARCH_DEBOUNCE_MS,
  CHAT_MESSAGE_MAX_LENGTH,
  CHAT_MESSAGES_PAGE_SIZE,
  CHAT_USERNAME_SEARCH_MAX,
} from '../config/constants';
import { mergeThreadPresenceFromDetail } from '../lib/mergeThreadPresenceFromDetail';
import { mergeThreadsWithLocalOnly } from '../lib/mergeThreadsWithLocalOnly';
import { canSendChatMessages, resolveCanLeave } from '../lib/chatCapabilities';
import { buildForwardMessageText, toMessageQuotePreview } from '../lib/messageQuote';
import {
  getEffectivePinnedForChat,
  isMessagePinned,
  mergePinnedMessages,
  normalizePinnedDtos,
  resolvePinnedMessages,
} from '../lib/pinnedMessages';
import {
  canDeleteStaffGroupChat,
  canManageStaffGroupChat,
  canToggleStaffGroupAdmin,
  canManageGroupParticipants,
  canPinChatMessage,
} from '../lib/groupChatPermissions';
import type { ChatPinnedMessageDTO, ChatMessageDTO } from '../api/dto';
import { chatReducer, initialChatState } from './reducer';
import type {
  ChatAttachment,
  ChatDetail,
  ChatListFilter,
  ChatMessage,
  ChatReplyPreview,
  ChatReportReason,
  ChatThread,
  ChatUser,
} from './types';
import { useChatSocketEvents } from '../realtime/useChatSocketEvents';

type UseChatControllerParams = {
  api: ChatApi;
  runtime: ChatRuntime;
  currentUserId: number;
  enabled: boolean;
  canManageGroups?: boolean;
};

export function useChatController({
  api,
  runtime,
  currentUserId,
  enabled,
  canManageGroups = false,
}: UseChatControllerParams) {
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const readRequestKeyRef = useRef<string>('');
  const availableUsersRequestIdRef = useRef(0);
  const chatDetailRequestIdRef = useRef(0);
  const chatDetailInFlightRef = useRef<Map<number, Promise<void>>>(new Map());
  const threadsPageRef = useRef(1);
  const threadsListRequestIdRef = useRef(0);
  const threadsRef = useRef(state.threads);
  threadsRef.current = state.threads;
  const excludedLocalThreadIdsRef = useRef<Set<number>>(new Set());
  const messagesByThreadRef = useRef(state.messagesByThread);
  messagesByThreadRef.current = state.messagesByThread;
  const lastActiveMessagesRefreshRef = useRef<number | null>(null);
  const [threadsSearch, setThreadsSearch] = useState('');
  const [debouncedThreadsSearch, setDebouncedThreadsSearch] = useState('');
  const [threadsHasMore, setThreadsHasMore] = useState(false);
  const [isThreadsLoadingMore, setIsThreadsLoadingMore] = useState(false);
  const messageAttachmentsRef = useRef<Map<string, File[]>>(new Map());
  const [isCreateChatModalOpen, setIsCreateChatModalOpen] = useState(false);
  const [isCreateGroupChatModalOpen, setIsCreateGroupChatModalOpen] = useState(false);
  const [availableUsersQuery, setAvailableUsersQuery] = useState('');
  const [groupParticipantsQuery, setGroupParticipantsQuery] = useState('');
  const [availableUsers, setAvailableUsers] = useState<ChatUser[]>([]);
  const [isAvailableUsersLoading, setIsAvailableUsersLoading] = useState(false);
  const [isCreatingPersonalChat, setIsCreatingPersonalChat] = useState(false);
  const [groupTitle, setGroupTitle] = useState('');
  const [groupAvatarFile, setGroupAvatarFile] = useState<File | null>(null);
  const [groupAvatarPreviewUrl, setGroupAvatarPreviewUrl] = useState<string | null>(null);
  const [selectedGroupParticipantIds, setSelectedGroupParticipantIds] = useState<number[]>([]);
  const [isCreatingGroupChat, setIsCreatingGroupChat] = useState(false);
  const [createGroupErrorMessage, setCreateGroupErrorMessage] = useState<string | null>(null);
  const [isEditGroupChatModalOpen, setIsEditGroupChatModalOpen] = useState(false);
  const [editGroupSnapshot, setEditGroupSnapshot] = useState<ChatDetail | null>(null);
  const [editGroupTitle, setEditGroupTitle] = useState('');
  const [editGroupStatus, setEditGroupStatus] = useState<'active' | 'closed'>('active');
  const [editGroupAvatarFile, setEditGroupAvatarFile] = useState<File | null>(null);
  const [editGroupAvatarPreviewUrl, setEditGroupAvatarPreviewUrl] = useState<string | null>(null);
  const [editRemovedUserIds, setEditRemovedUserIds] = useState<number[]>([]);
  const [editAddedUsers, setEditAddedUsers] = useState<ChatUser[]>([]);
  const [editParticipantsQuery, setEditParticipantsQuery] = useState('');
  const [isUpdatingGroupChat, setIsUpdatingGroupChat] = useState(false);
  const [updateGroupErrorMessage, setUpdateGroupErrorMessage] = useState<string | null>(null);
  const [isLeavingChat, setIsLeavingChat] = useState(false);
  const [leaveChatTarget, setLeaveChatTarget] = useState<{ chatId: number; title: string } | null>(
    null,
  );
  const [leaveChatErrorMessage, setLeaveChatErrorMessage] = useState<string | null>(null);
  const [isDeletingChat, setIsDeletingChat] = useState(false);
  const [deleteChatTarget, setDeleteChatTarget] = useState<{
    chatId: number;
    title: string;
  } | null>(null);
  const [deleteChatErrorMessage, setDeleteChatErrorMessage] = useState<string | null>(null);
  const [togglingAdminUserId, setTogglingAdminUserId] = useState<number | null>(null);
  const [adminToggleErrorMessage, setAdminToggleErrorMessage] = useState<string | null>(null);
  const [isTogglingArchive, setIsTogglingArchive] = useState(false);
  const [isTogglingChatStatus, setIsTogglingChatStatus] = useState(false);
  const [closeChatTarget, setCloseChatTarget] = useState<{ chatId: number; title: string } | null>(
    null,
  );
  const [closeChatErrorMessage, setCloseChatErrorMessage] = useState<string | null>(null);
  const [draftAttachments, setDraftAttachments] = useState<File[]>([]);
  const [replyToMessage, setReplyToMessage] = useState<ChatMessage | null>(null);
  const [pinnedMessagesByChatId, setPinnedMessagesByChatId] = useState<
    Record<number, ChatMessage[]>
  >({});
  const [isPinningMessage, setIsPinningMessage] = useState(false);
  const [reportTargetMessage, setReportTargetMessage] = useState<ChatMessage | null>(null);
  const [reportReason, setReportReason] = useState<ChatReportReason | ''>('');
  const [reportComment, setReportComment] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [reportErrorMessage, setReportErrorMessage] = useState<string | null>(null);
  const [isReportSuccessModalOpen, setIsReportSuccessModalOpen] = useState(false);
  const [forwardTargetMessage, setForwardTargetMessage] = useState<ChatMessage | null>(null);
  const [forwardQuery, setForwardQuery] = useState('');
  const [selectedForwardChatIds, setSelectedForwardChatIds] = useState<number[]>([]);
  const [isForwarding, setIsForwarding] = useState(false);
  const [forwardErrorMessage, setForwardErrorMessage] = useState<string | null>(null);
  const [composerFocusRequestId, setComposerFocusRequestId] = useState(0);
  const [chatDetailTarget, setChatDetailTarget] = useState<{
    chatId: number;
    title: string;
  } | null>(null);
  const [chatDetail, setChatDetail] = useState<ChatDetail | null>(null);
  const [chatDetailView, setChatDetailView] = useState<
    'overview' | 'photos' | 'videos' | 'files' | 'pinned'
  >('overview');
  const [removingParticipantUserId, setRemovingParticipantUserId] = useState<number | null>(null);
  const [removeParticipantTarget, setRemoveParticipantTarget] = useState<{
    chatId: number;
    userId: number;
    participantName: string;
  } | null>(null);
  const [removeParticipantErrorMessage, setRemoveParticipantErrorMessage] = useState<string | null>(
    null,
  );
  const [isChatDetailLoading, setIsChatDetailLoading] = useState(false);
  const [chatDetailErrorMessage, setChatDetailErrorMessage] = useState<string | null>(null);
  const [chatReportTarget, setChatReportTarget] = useState<{
    chatId: number;
    title: string;
  } | null>(null);
  const [chatReportReason, setChatReportReason] = useState<ChatReportReason | ''>('');
  const [chatReportComment, setChatReportComment] = useState('');
  const [isSubmittingChatReport, setIsSubmittingChatReport] = useState(false);
  const [chatReportErrorMessage, setChatReportErrorMessage] = useState<string | null>(null);

  const filteredThreads = state.threads;

  const activeId = useMemo(() => {
    if (!filteredThreads.length) return null;
    if (state.activeId && filteredThreads.some((thread) => thread.id === state.activeId)) {
      return state.activeId;
    }
    return filteredThreads[0].id;
  }, [filteredThreads, state.activeId]);

  const activeThread = useMemo(
    () => state.threads.find((thread) => thread.id === activeId) ?? null,
    [state.threads, activeId],
  );

  const activeGroupParticipantsCount = useMemo(() => {
    if (!activeThread || activeThread.kind !== 'group' || chatDetail?.id !== activeThread.id) {
      return undefined;
    }

    return chatDetail.participants.length;
  }, [activeThread, chatDetail]);

  const handlePinnedSocketUpdate = useCallback((chatId: number, messages: ChatMessage[] | null) => {
    setPinnedMessagesByChatId((prev) => ({
      ...prev,
      [chatId]: messages ?? [],
    }));
    setChatDetail((current) =>
      current?.id === chatId ? { ...current, pinnedMessage: messages?.[0] ?? null } : current,
    );
  }, []);

  const reconnectResyncRef = useRef<() => void>(() => {});

  const handleSocketReconnect = useCallback(() => {
    reconnectResyncRef.current();
  }, []);

  useChatSocketEvents({
    enabled,
    dispatch,
    currentUserId,
    activeChatId: activeId,
    filter: state.filter,
    runtime,
    threadsRef,
    onPinnedUpdate: handlePinnedSocketUpdate,
    onReconnect: handleSocketReconnect,
  });

  const activeMessagesBucket = activeId ? state.messagesByThread[activeId] : null;

  const messages = useMemo(() => activeMessagesBucket?.items ?? [], [activeMessagesBucket?.items]);

  const hasOlderMessages = !!activeMessagesBucket?.nextCursor;
  const isLoadingOlderMessages = !!activeMessagesBucket?.isLoadingOlder;

  const pinnedMessages = useMemo(() => {
    if (!activeId) return [];

    return resolvePinnedMessages(
      getEffectivePinnedForChat(activeId, pinnedMessagesByChatId, activeThread, chatDetail),
      messages,
    );
  }, [activeId, activeThread?.pinnedMessage, chatDetail, messages, pinnedMessagesByChatId]);

  const canPinMessages = useMemo(
    () => canPinChatMessage(activeThread, chatDetail, currentUserId, canManageGroups),
    [activeThread, canManageGroups, chatDetail, currentUserId],
  );

  const forwardableThreads = useMemo(() => {
    const normalizedQuery = forwardQuery.trim().toLowerCase();
    const sourceConversationId = forwardTargetMessage?.conversationId ?? null;

    return state.threads.filter((thread) => {
      if (thread.status !== 'active') return false;
      if (sourceConversationId && thread.id === sourceConversationId) return false;
      if (!normalizedQuery) return true;

      return [thread.title, thread.subtitle, thread.lastMessagePreview]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(normalizedQuery));
    });
  }, [forwardQuery, forwardTargetMessage, state.threads]);

  const toReplyPreview = useCallback(
    (message: ChatMessage): ChatReplyPreview => toMessageQuotePreview(message),
    [],
  );

  const createOptimisticAttachments = useCallback((files: File[]): ChatAttachment[] => {
    // Локальный файл ещё не отправленного сообщения: url — blob, живёт, пока открыта страница
    const localUrlExpiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    return files.map((file, index) => ({
      key: `local-${Date.now() + index + 1}`,
      name: file.name,
      url: URL.createObjectURL(file),
      thumbnailUrl: null,
      urlExpiresAt: localUrlExpiresAt,
      isLocal: true,
      size: file.size,
      mime: file.type || 'application/octet-stream',
      kind: file.type.startsWith('image/')
        ? 'image'
        : file.type.startsWith('video/')
          ? 'video'
          : 'file',
    }));
  }, []);

  /**
   * Пересылка перезаливает вложение: файл скачивается по presigned-ссылке и прикладывается к
   * новому сообщению. Ссылка — по ключу через роут файла; запрос к R2 идёт без токена и
   * cookies (Authorization ломает подпись), и private bucket должен разрешать CORS для домена
   * админки.
   */
  const cloneAttachmentToFile = useCallback(
    async (attachment: ChatAttachment) => {
      const fetchImpl = runtime.fetchImpl ?? fetch;
      const url = attachment.isLocal ? attachment.url : await getPrivateFileUrl(attachment);

      if (!url) {
        throw new Error(`Attachment ${attachment.name} is not available`);
      }

      const response = await fetchImpl(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch attachment ${attachment.name}`);
      }

      const blob = await response.blob();

      return new File([blob], attachment.name, {
        type: attachment.mime || blob.type || 'application/octet-stream',
      });
    },
    [runtime],
  );

  const mapListChatsResult = useCallback(
    (result: Awaited<ReturnType<ChatApi['listChats']>>) => {
      return result.chats.map((item) => {
        const thread = mapThread(item, runtime, currentUserId);
        return {
          ...thread,
          isArchived: state.filter === 'archived' || item.isArchived === true,
        };
      });
    },
    [currentUserId, runtime, state.filter],
  );

  const buildListChatsParams = useCallback(
    (page: number) => {
      const search = debouncedThreadsSearch.trim();

      const base = {
        search: search || undefined,
        page,
        pageSize: CHAT_LIST_PAGE_SIZE,
        sort: 'lastMessageAt:desc' as const,
      };

      if (state.filter === 'archived') {
        return base;
      }

      return {
        ...base,
        type: state.filter === 'all' ? undefined : state.filter,
      };
    },
    [debouncedThreadsSearch, state.filter],
  );

  const syncPinnedFromThreads = useCallback((threads: ChatThread[]) => {
    setPinnedMessagesByChatId((prev) => {
      const next = { ...prev };

      threads.forEach((thread) => {
        next[thread.id] = thread.pinnedMessage ? [thread.pinnedMessage] : [];
      });

      return next;
    });
  }, []);

  const fetchThreadsPage = useCallback(
    async (page: number, requestId: number) => {
      const params = buildListChatsParams(page);
      const result =
        state.filter === 'archived'
          ? await api.listArchivedChats(params)
          : await api.listChats(params);

      if (requestId !== threadsListRequestIdRef.current) {
        return null;
      }

      const threads = mapListChatsResult(result);
      const hasMore = result.pagination.page < result.pagination.pageCount;

      return { threads, hasMore };
    },
    [api, buildListChatsParams, mapListChatsResult],
  );

  const loadThreads = useCallback(async () => {
    const requestId = ++threadsListRequestIdRef.current;
    threadsPageRef.current = 1;
    dispatch({ type: 'threads/loading', payload: true });

    try {
      const pageResult = await fetchThreadsPage(1, requestId);

      if (!pageResult) {
        return;
      }

      setThreadsHasMore(pageResult.hasMore);
      syncPinnedFromThreads(pageResult.threads);
      const apiThreadIds = new Set(pageResult.threads.map((thread) => thread.id));
      excludedLocalThreadIdsRef.current.forEach((chatId) => {
        if (apiThreadIds.has(chatId)) {
          excludedLocalThreadIdsRef.current.delete(chatId);
        }
      });
      dispatch({
        type: 'threads/replace',
        payload: mergeThreadsWithLocalOnly(
          pageResult.threads,
          threadsRef.current,
          state.filter,
          excludedLocalThreadIdsRef.current,
        ),
      });
    } finally {
      if (requestId === threadsListRequestIdRef.current) {
        dispatch({ type: 'threads/loading', payload: false });
      }
    }
  }, [fetchThreadsPage, state.filter, syncPinnedFromThreads]);

  const loadMoreThreads = useCallback(async () => {
    if (!threadsHasMore || isThreadsLoadingMore || state.isThreadsLoading) {
      return;
    }

    const requestId = threadsListRequestIdRef.current;
    const nextPage = threadsPageRef.current + 1;

    setIsThreadsLoadingMore(true);

    try {
      const pageResult = await fetchThreadsPage(nextPage, requestId);

      if (!pageResult) {
        return;
      }

      threadsPageRef.current = nextPage;
      setThreadsHasMore(pageResult.hasMore);
      syncPinnedFromThreads(pageResult.threads);
      dispatch({ type: 'threads/append', payload: pageResult.threads });
    } finally {
      if (requestId === threadsListRequestIdRef.current) {
        setIsThreadsLoadingMore(false);
      }
    }
  }, [
    fetchThreadsPage,
    isThreadsLoadingMore,
    state.isThreadsLoading,
    syncPinnedFromThreads,
    threadsHasMore,
  ]);

  const refreshThreadsFirstPage = useCallback(async () => {
    const requestId = threadsListRequestIdRef.current;

    try {
      const pageResult = await fetchThreadsPage(1, requestId);

      if (!pageResult) {
        return;
      }

      setThreadsHasMore(pageResult.hasMore);
      syncPinnedFromThreads(pageResult.threads);
      const apiThreadIds = new Set(pageResult.threads.map((thread) => thread.id));
      excludedLocalThreadIdsRef.current.forEach((chatId) => {
        if (apiThreadIds.has(chatId)) {
          excludedLocalThreadIdsRef.current.delete(chatId);
        }
      });

      if (threadsPageRef.current <= 1) {
        dispatch({
          type: 'threads/replace',
          payload: mergeThreadsWithLocalOnly(
            pageResult.threads,
            threadsRef.current,
            state.filter,
            excludedLocalThreadIdsRef.current,
          ),
        });
        return;
      }

      const pageIds = new Set(pageResult.threads.map((thread) => thread.id));
      const rest = threadsRef.current.filter((thread) => !pageIds.has(thread.id));

      dispatch({
        type: 'threads/replace',
        payload: [
          ...mergeThreadsWithLocalOnly(
            pageResult.threads,
            threadsRef.current,
            state.filter,
            excludedLocalThreadIdsRef.current,
          ),
          ...rest,
        ],
      });
    } catch {
      // keep current list on poll failure
    }
  }, [fetchThreadsPage, state.filter, syncPinnedFromThreads]);

  const mapMessagesPageAscending = useCallback(
    (chatId: number, page: ChatMessageDTO[]) =>
      page
        .slice()
        .reverse()
        .map((item) => mapMessage(item, chatId, currentUserId, runtime)),
    [currentUserId, runtime],
  );

  const applyPinnedFromServer = useCallback(
    (
      chatId: number,
      pinnedMessage: ChatPinnedMessageDTO | null | undefined,
      pinnedMessages?: ChatPinnedMessageDTO[] | null,
    ) => {
      const hasPinnedField = pinnedMessage !== undefined || pinnedMessages !== undefined;

      if (!hasPinnedField) {
        return;
      }

      const dtos = normalizePinnedDtos(pinnedMessage, pinnedMessages);

      if (!dtos.length) {
        setPinnedMessagesByChatId((prev) => ({
          ...prev,
          [chatId]: [],
        }));
        dispatch({
          type: 'threads/setPinnedMessage',
          payload: { chatId, pinnedMessage: null },
        });
        setChatDetail((current) =>
          current?.id === chatId ? { ...current, pinnedMessage: null } : current,
        );
        return;
      }

      const mapped = dtos.map((dto) => mapPinnedMessage(dto, chatId, currentUserId, runtime));

      setPinnedMessagesByChatId((prev) => ({
        ...prev,
        [chatId]: mapped,
      }));
      dispatch({
        type: 'threads/setPinnedMessage',
        payload: { chatId, pinnedMessage: mapped[0] },
      });
      setChatDetail((current) =>
        current?.id === chatId ? { ...current, pinnedMessage: mapped[0] } : current,
      );
    },
    [currentUserId, runtime],
  );

  const syncPinnedFromMessagesResult = applyPinnedFromServer;

  const fetchParticipantCandidates = useCallback(
    async (search: string) => {
      const requestId = ++availableUsersRequestIdRef.current;
      const trimmed = search.trim().slice(0, CHAT_USERNAME_SEARCH_MAX);
      setIsAvailableUsersLoading(true);

      try {
        const whitelistResult = trimmed
          ? await api.listAvailableUsers({ search: trimmed })
          : await api.listAvailableUsers();

        const globalResult = trimmed ? await api.searchUsers(trimmed) : { users: [] };

        if (requestId !== availableUsersRequestIdRef.current) {
          return;
        }

        const seen = new Set<number>();
        const merged = [...whitelistResult.users, ...globalResult.users].filter((user) => {
          if (seen.has(user.id)) {
            return false;
          }

          seen.add(user.id);
          return true;
        });

        setAvailableUsers(merged.map((item) => mapChatUser(item, runtime)));
      } catch {
        if (requestId === availableUsersRequestIdRef.current) {
          setAvailableUsers([]);
        }
      } finally {
        if (requestId === availableUsersRequestIdRef.current) {
          setIsAvailableUsersLoading(false);
        }
      }
    },
    [api, runtime],
  );

  const loadInitialMessages = useCallback(
    async (chatId: number) => {
      dispatch({ type: 'messages/loading', payload: { chatId, value: true } });

      try {
        const result = await api.listMessages(chatId, { limit: CHAT_MESSAGES_PAGE_SIZE });
        syncPinnedFromMessagesResult(chatId, result.pinnedMessage, result.pinnedMessages);
        const items = mapMessagesPageAscending(chatId, result.messages);

        dispatch({
          type: 'messages/replace',
          payload: {
            chatId,
            items,
            nextCursor: result.nextCursor,
          },
        });
      } catch {
        dispatch({ type: 'messages/loading', payload: { chatId, value: false } });
      }
    },
    [api, mapMessagesPageAscending, syncPinnedFromMessagesResult],
  );

  const loadOlderMessages = useCallback(async () => {
    const chatId = activeId;
    if (!chatId) {
      return;
    }

    const bucket = state.messagesByThread[chatId];
    if (!bucket?.nextCursor || bucket.isLoadingOlder || bucket.isLoading || !bucket.isLoaded) {
      return;
    }

    dispatch({ type: 'messages/loadingOlder', payload: { chatId, value: true } });

    try {
      const result = await api.listMessages(chatId, {
        beforeMessageId: bucket.nextCursor,
        limit: CHAT_MESSAGES_PAGE_SIZE,
      });

      syncPinnedFromMessagesResult(chatId, result.pinnedMessage, result.pinnedMessages);

      dispatch({
        type: 'messages/prepend',
        payload: {
          chatId,
          items: mapMessagesPageAscending(chatId, result.messages),
          nextCursor: result.nextCursor,
        },
      });
    } catch {
      dispatch({ type: 'messages/loadingOlder', payload: { chatId, value: false } });
    }
  }, [
    activeId,
    api,
    mapMessagesPageAscending,
    state.messagesByThread,
    syncPinnedFromMessagesResult,
  ]);

  const refreshLatestMessages = useCallback(
    async (chatId: number) => {
      const result = await api.listMessages(chatId, { limit: CHAT_MESSAGES_PAGE_SIZE });
      syncPinnedFromMessagesResult(chatId, result.pinnedMessage, result.pinnedMessages);
      const items = mapMessagesPageAscending(chatId, result.messages);

      dispatch({
        type: 'messages/upsert',
        payload: {
          chatId,
          items,
        },
      });
    },
    [api, mapMessagesPageAscending, syncPinnedFromMessagesResult],
  );

  const loadChatDetail = useCallback(
    async (chatId: number) => {
      const requestId = ++chatDetailRequestIdRef.current;
      setIsChatDetailLoading(true);
      setChatDetailErrorMessage(null);

      try {
        const result = await api.getChatDetail(chatId);

        if (requestId !== chatDetailRequestIdRef.current) {
          return;
        }

        const mappedDetail = mapChatDetail(result, runtime, currentUserId);
        setChatDetail(mappedDetail);
        setPinnedMessagesByChatId((prev) => ({
          ...prev,
          [chatId]: mappedDetail.pinnedMessage ? [mappedDetail.pinnedMessage] : [],
        }));
        dispatch({
          type: 'threads/setPinnedMessage',
          payload: { chatId, pinnedMessage: mappedDetail.pinnedMessage },
        });

        const thread = threadsRef.current.find((item) => item.id === chatId);
        if (thread) {
          const withPresence = mergeThreadPresenceFromDetail(thread, mappedDetail, currentUserId);
          if (withPresence !== thread) {
            dispatch({ type: 'threads/upsert', payload: withPresence });
          }
        }
      } catch (error) {
        if (requestId !== chatDetailRequestIdRef.current) {
          return;
        }

        const apiError = error as ChatApiError | undefined;
        const data =
          apiError &&
          typeof apiError === 'object' &&
          apiError.data &&
          typeof apiError.data === 'object'
            ? (apiError.data as Record<string, unknown>)
            : null;
        const message =
          (typeof data?.message === 'string' && data.message) || 'Failed to load chat details';

        setChatDetail(null);
        setChatDetailErrorMessage(message);
      } finally {
        if (requestId === chatDetailRequestIdRef.current) {
          setIsChatDetailLoading(false);
        }
      }
    },
    [api, currentUserId, runtime],
  );

  const ensureChatDetailLoaded = useCallback(
    async (chatId: number) => {
      if (chatDetail?.id === chatId) {
        return;
      }

      const existing = chatDetailInFlightRef.current.get(chatId);
      if (existing) {
        await existing;
        return;
      }

      const p = (async () => {
        try {
          await loadChatDetail(chatId);
        } finally {
          chatDetailInFlightRef.current.delete(chatId);
        }
      })();

      chatDetailInFlightRef.current.set(chatId, p);
      await p;
    },
    [chatDetail?.id, loadChatDetail],
  );

  const ensureActiveChatDetailLoaded = useCallback(async () => {
    if (!activeThread || activeThread.kind !== 'group') {
      return;
    }

    await ensureChatDetailLoaded(activeThread.id);
  }, [activeThread, ensureChatDetailLoaded]);

  useEffect(() => {
    chatDetailRequestIdRef.current += 1;
    setChatDetailTarget(null);
    setChatDetail(null);
    setChatDetailView('overview');
    setIsChatDetailLoading(false);
    setChatDetailErrorMessage(null);
    setChatReportTarget(null);
    setChatReportReason('');
    setChatReportComment('');
    setChatReportErrorMessage(null);
    setIsSubmittingChatReport(false);
  }, [activeId]);

  useEffect(() => {
    if (!enabled || !activeId) {
      return;
    }

    const thread = threadsRef.current.find((item) => item.id === activeId);
    if (!thread) {
      return;
    }

    setPinnedMessagesByChatId((prev) => ({
      ...prev,
      [activeId]: thread.pinnedMessage ? [thread.pinnedMessage] : [],
    }));

    if (thread.kind === 'group') {
      void loadChatDetail(activeId);
    }
  }, [activeId, enabled, loadChatDetail]);

  const submitMessage = useCallback(
    async ({
      chatId,
      text,
      clientMessageId,
      attachments,
      replyToMessageId,
      mode,
    }: {
      chatId: number;
      text: string;
      clientMessageId: string;
      attachments?: File[];
      replyToMessageId?: number;
      mode: 'new' | 'retry';
    }) => {
      if (mode === 'retry') {
        dispatch({
          type: 'message/retry',
          payload: {
            chatId,
            clientMessageId,
          },
        });
      }

      try {
        const result = await api.createMessage({
          chatId,
          text,
          clientMessageId,
          attachments,
          replyToMessageId,
        });

        dispatch({
          type: 'message/confirm',
          payload: {
            chatId,
            clientMessageId,
            item: mapMessage(result, chatId, currentUserId, runtime),
          },
        });

        messageAttachmentsRef.current.delete(clientMessageId);
        await refreshThreadsFirstPage();
      } catch (error) {
        const apiError = error as ChatApiError | undefined;
        const hasHttpStatus =
          !!apiError && typeof apiError === 'object' && typeof apiError.status === 'number';

        if (hasHttpStatus) {
          dispatch({
            type: 'message/fail',
            payload: {
              chatId,
              clientMessageId,
            },
          });
          return;
        }

        try {
          await refreshLatestMessages(chatId);
          await refreshThreadsFirstPage();
        } catch {}
      }
    },
    [api, currentUserId, refreshLatestMessages, refreshThreadsFirstPage, runtime],
  );

  useEffect(() => {
    if (!enabled || !activeId) return;

    const bucket = messagesByThreadRef.current[activeId];
    if (!bucket?.isLoaded && !bucket?.isLoading) {
      void loadInitialMessages(activeId);
      return;
    }

    if (bucket.isLoaded && lastActiveMessagesRefreshRef.current !== activeId) {
      lastActiveMessagesRefreshRef.current = activeId;
      void refreshLatestMessages(activeId);
    }
  }, [enabled, activeId, loadInitialMessages, refreshLatestMessages]);

  useEffect(() => {
    setReplyToMessage(null);
  }, [activeId]);

  useEffect(() => {
    setForwardTargetMessage(null);
    setForwardQuery('');
    setSelectedForwardChatIds([]);
    setForwardErrorMessage(null);
  }, [activeId]);

  const participantPickerOpen =
    isCreateChatModalOpen || isCreateGroupChatModalOpen || isEditGroupChatModalOpen;
  const participantPickerQuery = isEditGroupChatModalOpen
    ? editParticipantsQuery
    : isCreateGroupChatModalOpen
      ? groupParticipantsQuery
      : availableUsersQuery;

  const canManageActiveGroup = useMemo(
    () => canManageStaffGroupChat(chatDetail, currentUserId, canManageGroups),
    [canManageGroups, chatDetail, currentUserId],
  );

  const canManageActiveGroupParticipants = useMemo(
    () => canManageGroupParticipants(chatDetail, currentUserId, canManageGroups),
    [canManageGroups, chatDetail, currentUserId],
  );

  const canDeleteActiveGroup = useMemo(
    () => canDeleteStaffGroupChat(chatDetail, currentUserId, canManageGroups),
    [canManageGroups, chatDetail, currentUserId],
  );

  const canToggleGroupAdmin = useMemo(
    () => canToggleStaffGroupAdmin(chatDetail, currentUserId, canManageGroups),
    [canManageGroups, chatDetail, currentUserId],
  );

  const activeCanLeave = useMemo(
    () => resolveCanLeave(activeThread, chatDetail),
    [activeThread, chatDetail],
  );

  const canSendMessages = useMemo(
    () => canSendChatMessages(activeThread, chatDetail),
    [activeThread, chatDetail],
  );

  const composerPlaceholder = useMemo(() => {
    if (!activeThread) {
      return 'Write a message...';
    }

    if (activeThread.status === 'closed') {
      return 'This chat is closed';
    }

    if (chatDetail?.mutedUntil && chatDetail.mutedUntil.getTime() > Date.now()) {
      return 'You cannot send messages in this chat';
    }

    if (activeThread.mutedUntil && activeThread.mutedUntil.getTime() > Date.now()) {
      return 'You cannot send messages in this chat';
    }

    return 'Write a message...';
  }, [activeThread, chatDetail?.mutedUntil]);

  const canSaveGroupEdit = useMemo(() => {
    if (!editGroupSnapshot || !editGroupTitle.trim()) {
      return false;
    }

    const titleChanged = editGroupTitle.trim() !== editGroupSnapshot.title;
    const statusChanged = editGroupStatus !== editGroupSnapshot.status;
    const avatarChanged = !!editGroupAvatarFile;
    const membersChanged = editRemovedUserIds.length > 0 || editAddedUsers.length > 0;

    return titleChanged || statusChanged || avatarChanged || membersChanged;
  }, [
    editAddedUsers.length,
    editGroupAvatarFile,
    editGroupSnapshot,
    editGroupStatus,
    editGroupTitle,
    editRemovedUserIds.length,
  ]);

  useEffect(() => {
    if (!enabled || !participantPickerOpen) return;

    const timer = window.setTimeout(() => {
      void fetchParticipantCandidates(participantPickerQuery);
    }, 250);

    return () => {
      window.clearTimeout(timer);
    };
  }, [enabled, fetchParticipantCandidates, participantPickerOpen, participantPickerQuery]);

  useEffect(() => {
    if (!groupAvatarFile) {
      setGroupAvatarPreviewUrl(null);
      return;
    }

    const previewUrl = URL.createObjectURL(groupAvatarFile);
    setGroupAvatarPreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [groupAvatarFile]);

  useEffect(() => {
    if (!editGroupAvatarFile) {
      setEditGroupAvatarPreviewUrl(editGroupSnapshot?.avatarUrl ?? null);
      return;
    }

    const previewUrl = URL.createObjectURL(editGroupAvatarFile);
    setEditGroupAvatarPreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [editGroupAvatarFile, editGroupSnapshot?.avatarUrl]);

  const pollTick = useCallback(async () => {
    await refreshThreadsFirstPage();

    if (activeId) {
      await refreshLatestMessages(activeId);
    }
  }, [activeId, refreshLatestMessages, refreshThreadsFirstPage]);

  useEffect(() => {
    reconnectResyncRef.current = () => {
      void pollTick();
    };
  }, [pollTick]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedThreadsSearch(threadsSearch.trim());
    }, CHAT_LIST_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [threadsSearch]);

  useEffect(() => {
    if (!enabled) return;
    void loadThreads();
  }, [debouncedThreadsSearch, enabled, loadThreads, state.filter]);

  useEffect(() => {
    setThreadsSearch('');
    setDebouncedThreadsSearch('');
  }, [state.filter]);

  useEffect(() => {
    if (!activeId) return;

    const unreadIds = [
      ...new Set(
        messages.filter((msg) => !msg.isOwn && !msg.readByMe && msg.id > 0).map((msg) => msg.id),
      ),
    ];

    if (!unreadIds.length) return;

    const key = `${activeId}:${unreadIds.join(',')}`;
    if (readRequestKeyRef.current === key) return;

    readRequestKeyRef.current = key;

    void api
      .markRead(activeId, unreadIds)
      .then((result) => {
        dispatch({
          type: 'messages/markRead',
          payload: {
            chatId: result.chatId,
            messageIds: result.readMessageIds,
            unreadCount: result.unreadCount,
          },
        });
      })
      .catch(() => {
        if (readRequestKeyRef.current === key) {
          readRequestKeyRef.current = '';
        }
      });
  }, [activeId, api, messages]);

  const send = useCallback(async () => {
    const chatId = activeId;
    const text = state.draft.trim();
    const attachments = draftAttachments;
    const nextReplyToMessage = replyToMessage;

    if (!chatId || state.draft.length > CHAT_MESSAGE_MAX_LENGTH) return;
    if (!text && attachments.length === 0) return;

    const clientMessageId = nanoid();
    if (attachments.length > 0) {
      messageAttachmentsRef.current.set(clientMessageId, attachments);
      setDraftAttachments([]);
    }

    dispatch({
      type: 'message/optimisticAdd',
      payload: {
        chatId,
        item: {
          id: -Date.now(),
          kind: 'user',
          conversationId: chatId,
          authorId: currentUserId,
          authorName: 'You',
          sentAt: new Date(),
          text: text || null,
          attachments: createOptimisticAttachments(attachments),
          replyTo: nextReplyToMessage ? toReplyPreview(nextReplyToMessage) : null,
          readByMe: true,
          isOwn: true,
          outgoingStatus: 'sent',
          pending: true,
          clientMessageId,
        },
      },
    });

    setReplyToMessage(null);

    await submitMessage({
      chatId,
      text,
      clientMessageId,
      attachments,
      replyToMessageId: nextReplyToMessage?.id,
      mode: 'new',
    });
  }, [
    activeId,
    createOptimisticAttachments,
    currentUserId,
    draftAttachments,
    replyToMessage,
    state.draft,
    submitMessage,
    toReplyPreview,
  ]);

  const retryMessage = useCallback(
    async (message: ChatMessage) => {
      if (!message.failed || !message.clientMessageId) return;
      const attachments = messageAttachmentsRef.current.get(message.clientMessageId) ?? [];
      const text = message.text?.trim() ?? '';
      if (text.length > CHAT_MESSAGE_MAX_LENGTH) return;
      if (!text && attachments.length === 0) return;

      await submitMessage({
        chatId: message.conversationId,
        text,
        clientMessageId: message.clientMessageId,
        attachments,
        replyToMessageId: message.replyTo?.id,
        mode: 'retry',
      });
    },
    [submitMessage],
  );

  const attachFiles = useCallback((files: File[]) => {
    if (!files.length) return;
    setDraftAttachments((prev) => [...prev, ...files]);
  }, []);

  const removeDraftAttachment = useCallback((index: number) => {
    setDraftAttachments((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  }, []);

  const startReply = useCallback((message: ChatMessage) => {
    setReplyToMessage(message);
    setComposerFocusRequestId((value) => value + 1);
  }, []);

  const clearReply = useCallback(() => {
    setReplyToMessage(null);
  }, []);

  const setPinnedListForChat = useCallback((chatId: number, pinned: ChatMessage[]) => {
    setPinnedMessagesByChatId((prev) => ({
      ...prev,
      [chatId]: pinned,
    }));
  }, []);

  const refreshChatPins = useCallback(
    async (chatId: number) => {
      const [messagesResult] = await Promise.all([
        api.listMessages(chatId, { limit: CHAT_MESSAGES_PAGE_SIZE }),
        loadThreads(),
      ]);

      syncPinnedFromMessagesResult(
        chatId,
        messagesResult.pinnedMessage,
        messagesResult.pinnedMessages,
      );

      const items = mapMessagesPageAscending(chatId, messagesResult.messages);

      dispatch({
        type: 'messages/upsert',
        payload: {
          chatId,
          items,
        },
      });
    },
    [api, loadThreads, mapMessagesPageAscending, syncPinnedFromMessagesResult],
  );

  const resolveThreadForChat = useCallback(
    (chatId: number) => {
      if (activeId === chatId) {
        return activeThread;
      }

      return state.threads.find((item) => item.id === chatId);
    },
    [activeId, activeThread, state.threads],
  );

  const togglePinnedMessage = useCallback(
    async (message: ChatMessage) => {
      const chatId = message.conversationId;
      if (!canPinMessages || isPinningMessage || message.kind !== 'user') {
        return;
      }

      const thread = resolveThreadForChat(chatId);
      const effectivePinned = getEffectivePinnedForChat(
        chatId,
        pinnedMessagesByChatId,
        thread,
        chatDetail?.id === chatId ? chatDetail : null,
      );
      const localPinned = pinnedMessagesByChatId[chatId] ?? [];
      const sourceMessage = messages.find((item) => item.id === message.id) ?? message;
      const alreadyPinned = isMessagePinned(effectivePinned, message.id);

      setIsPinningMessage(true);

      try {
        if (alreadyPinned) {
          setPinnedListForChat(chatId, []);
          dispatch({
            type: 'threads/setPinnedMessage',
            payload: { chatId, pinnedMessage: null },
          });
          setChatDetail((current) =>
            current?.id === chatId ? { ...current, pinnedMessage: null } : current,
          );
          await api.unpinMessage(chatId);
          await refreshChatPins(chatId);
        } else {
          await api.pinMessage(chatId, message.id);
          setPinnedListForChat(chatId, mergePinnedMessages(localPinned, [sourceMessage]));
          await refreshChatPins(chatId);
        }
      } finally {
        setIsPinningMessage(false);
      }
    },
    [
      api,
      canPinMessages,
      chatDetail,
      isPinningMessage,
      messages,
      pinnedMessagesByChatId,
      refreshChatPins,
      resolveThreadForChat,
      setPinnedListForChat,
    ],
  );

  const unpinPinnedMessage = useCallback(
    async (message: ChatMessage) => {
      const chatId = message.conversationId;
      if (!canPinMessages || isPinningMessage) {
        return;
      }

      const thread = resolveThreadForChat(chatId);
      const effectivePinned = getEffectivePinnedForChat(
        chatId,
        pinnedMessagesByChatId,
        thread,
        chatDetail?.id === chatId ? chatDetail : null,
      );

      if (!isMessagePinned(effectivePinned, message.id)) {
        return;
      }

      setIsPinningMessage(true);

      try {
        setPinnedListForChat(chatId, []);
        dispatch({
          type: 'threads/setPinnedMessage',
          payload: { chatId, pinnedMessage: null },
        });
        setChatDetail((current) =>
          current?.id === chatId ? { ...current, pinnedMessage: null } : current,
        );
        await api.unpinMessage(chatId);
        await refreshChatPins(chatId);
      } finally {
        setIsPinningMessage(false);
      }
    },
    [
      api,
      canPinMessages,
      chatDetail,
      isPinningMessage,
      pinnedMessagesByChatId,
      refreshChatPins,
      resolveThreadForChat,
      setPinnedListForChat,
    ],
  );

  const openReportMessage = useCallback((message: ChatMessage) => {
    setIsReportSuccessModalOpen(false);
    setReportTargetMessage(message);
    setReportReason('');
    setReportComment('');
    setReportErrorMessage(null);
  }, []);

  const closeReportModal = useCallback(() => {
    if (isSubmittingReport) return;
    setReportTargetMessage(null);
    setReportReason('');
    setReportComment('');
    setReportErrorMessage(null);
    setComposerFocusRequestId((value) => value + 1);
  }, [isSubmittingReport]);

  const updateReportReason = useCallback((value: ChatReportReason | '') => {
    setReportReason(value);
    setReportErrorMessage(null);
  }, []);

  const updateReportComment = useCallback((value: string) => {
    setReportComment(value);
    setReportErrorMessage(null);
  }, []);

  const openChatDetails = useCallback(() => {
    if (!activeThread) return;
    setChatDetailTarget({
      chatId: activeThread.id,
      title: activeThread.title,
    });
    setChatDetailView('overview');
    setChatDetailErrorMessage(null);
    setAdminToggleErrorMessage(null);
    if (chatDetail?.id !== activeThread.id) {
      setChatDetail(null);
    }
    void loadChatDetail(activeThread.id);
  }, [activeThread, chatDetail?.id, loadChatDetail]);

  const closeChatDetails = useCallback(() => {
    chatDetailRequestIdRef.current += 1;
    setChatDetailTarget(null);
    setChatDetailView('overview');
    setIsChatDetailLoading(false);
    setChatDetailErrorMessage(null);
  }, []);

  const openChatDetailView = useCallback((view: 'photos' | 'videos' | 'files' | 'pinned') => {
    setChatDetailView(view);
  }, []);

  const goBackFromChatDetailView = useCallback(() => {
    setChatDetailView('overview');
  }, []);

  const openChatReportModal = useCallback(() => {
    if (!activeThread) return;
    setIsReportSuccessModalOpen(false);
    setChatReportTarget({
      chatId: activeThread.id,
      title: activeThread.title,
    });
    setChatReportReason('');
    setChatReportComment('');
    setChatReportErrorMessage(null);
  }, [activeThread]);

  const closeChatReportModal = useCallback(() => {
    if (isSubmittingChatReport) return;
    setChatReportTarget(null);
    setChatReportReason('');
    setChatReportComment('');
    setChatReportErrorMessage(null);
  }, [isSubmittingChatReport]);

  const updateChatReportReason = useCallback((value: ChatReportReason | '') => {
    setChatReportReason(value);
    setChatReportErrorMessage(null);
  }, []);

  const updateChatReportComment = useCallback((value: string) => {
    setChatReportComment(value);
    setChatReportErrorMessage(null);
  }, []);

  const closeReportSuccessModal = useCallback(() => {
    setIsReportSuccessModalOpen(false);
    setComposerFocusRequestId((value) => value + 1);
  }, []);

  const openForwardMessage = useCallback((message: ChatMessage) => {
    setForwardTargetMessage(message);
    setForwardQuery('');
    setSelectedForwardChatIds([]);
    setForwardErrorMessage(null);
  }, []);

  const closeForwardModal = useCallback(() => {
    if (isForwarding) return;
    setForwardTargetMessage(null);
    setForwardQuery('');
    setSelectedForwardChatIds([]);
    setForwardErrorMessage(null);
    setComposerFocusRequestId((value) => value + 1);
  }, [isForwarding]);

  const updateForwardQuery = useCallback((value: string) => {
    setForwardQuery(value);
    setForwardErrorMessage(null);
  }, []);

  const toggleForwardChat = useCallback((chatId: number) => {
    setSelectedForwardChatIds((prev) =>
      prev.includes(chatId) ? prev.filter((id) => id !== chatId) : [...prev, chatId],
    );
    setForwardErrorMessage(null);
  }, []);

  const submitReport = useCallback(async () => {
    if (!reportTargetMessage || !reportReason || isSubmittingReport) {
      return;
    }

    if (reportTargetMessage.isOwn) {
      setReportErrorMessage('You cannot report your own message');
      return;
    }

    if (reportTargetMessage.id <= 0) {
      setReportErrorMessage('Message is not available for reporting yet');
      return;
    }

    setIsSubmittingReport(true);
    setReportErrorMessage(null);

    try {
      await api.createReport({
        messageId: reportTargetMessage.id,
        reason: reportReason,
        comment: reportComment,
      });
      setIsReportSuccessModalOpen(true);
      setReportTargetMessage(null);
      setReportReason('');
      setReportComment('');
      setReportErrorMessage(null);
    } catch (error) {
      const apiError = error as ChatApiError | undefined;
      const data =
        apiError &&
        typeof apiError === 'object' &&
        apiError.data &&
        typeof apiError.data === 'object'
          ? (apiError.data as Record<string, unknown>)
          : null;
      if (apiError?.status === 409) {
        setReportErrorMessage(
          (typeof data?.message === 'string' && data.message) ||
            'This message has already been reported recently',
        );
        return;
      }
      const message =
        (typeof data?.message === 'string' && data.message) || 'Failed to submit report';

      setReportErrorMessage(message);
    } finally {
      setIsSubmittingReport(false);
    }
  }, [api, isSubmittingReport, reportComment, reportReason, reportTargetMessage]);

  const submitForward = useCallback(async () => {
    if (!forwardTargetMessage || !selectedForwardChatIds.length || isForwarding) {
      return;
    }

    setIsForwarding(true);
    setForwardErrorMessage(null);

    try {
      const files =
        forwardTargetMessage.attachments.length > 0
          ? await Promise.all(
              forwardTargetMessage.attachments.map((attachment) =>
                cloneAttachmentToFile(attachment),
              ),
            )
          : [];

      const forwardText = buildForwardMessageText(forwardTargetMessage);

      const createdMessages = await Promise.all(
        selectedForwardChatIds.map(async (chatId) => {
          const result = await api.createMessage({
            chatId,
            text: forwardText,
            attachments: files.length ? files : undefined,
          });

          return mapMessage(result, chatId, currentUserId, runtime);
        }),
      );

      const targetChatIds = [...new Set(createdMessages.map((item) => item.conversationId))];

      createdMessages.forEach((item) => {
        dispatch({
          type: 'messages/upsert',
          payload: {
            chatId: item.conversationId,
            items: [item],
          },
        });
      });

      setForwardTargetMessage(null);
      setForwardQuery('');
      setSelectedForwardChatIds([]);
      setForwardErrorMessage(null);
      await loadThreads();
      await Promise.all(targetChatIds.map((chatId) => refreshLatestMessages(chatId)));
      setComposerFocusRequestId((value) => value + 1);
    } catch (error) {
      const apiError = error as ChatApiError | undefined;
      const data =
        apiError &&
        typeof apiError === 'object' &&
        apiError.data &&
        typeof apiError.data === 'object'
          ? (apiError.data as Record<string, unknown>)
          : null;
      const message =
        (typeof data?.message === 'string' && data.message) ||
        (error instanceof Error && error.message) ||
        'Failed to forward message';

      setForwardErrorMessage(message);
    } finally {
      setIsForwarding(false);
    }
  }, [
    api,
    cloneAttachmentToFile,
    currentUserId,
    forwardTargetMessage,
    isForwarding,
    loadThreads,
    refreshLatestMessages,
    runtime,
    selectedForwardChatIds,
  ]);

  const submitChatReport = useCallback(async () => {
    if (!chatReportTarget || !chatReportReason || isSubmittingChatReport) {
      return;
    }

    setIsSubmittingChatReport(true);
    setChatReportErrorMessage(null);

    try {
      await api.createChatReport({
        chatId: chatReportTarget.chatId,
        reason: chatReportReason,
        comment: chatReportComment,
      });
      setIsReportSuccessModalOpen(true);
      setChatReportTarget(null);
      setChatReportReason('');
      setChatReportComment('');
      setChatReportErrorMessage(null);
    } catch (error) {
      const apiError = error as ChatApiError | undefined;
      const data =
        apiError &&
        typeof apiError === 'object' &&
        apiError.data &&
        typeof apiError.data === 'object'
          ? (apiError.data as Record<string, unknown>)
          : null;
      if (apiError?.status === 409) {
        setChatReportErrorMessage(
          (typeof data?.message === 'string' && data.message) ||
            'This chat has already been reported recently',
        );
        return;
      }
      const message =
        (typeof data?.message === 'string' && data.message) || 'Failed to submit report';

      setChatReportErrorMessage(message);
    } finally {
      setIsSubmittingChatReport(false);
    }
  }, [api, chatReportComment, chatReportReason, chatReportTarget, isSubmittingChatReport]);

  const selectChat = useCallback((chatId: number) => {
    dispatch({ type: 'active/set', payload: chatId });
  }, []);

  const setFilter = useCallback((value: ChatListFilter) => {
    threadsListRequestIdRef.current += 1;
    dispatch({ type: 'filter/set', payload: value });
  }, []);

  const toggleActiveChatArchive = useCallback(async () => {
    if (!activeThread || isTogglingArchive) {
      return;
    }

    const chatId = activeThread.id;

    setIsTogglingArchive(true);

    try {
      const result = await api.toggleChatArchive(chatId);

      const leftCurrentView =
        (state.filter === 'archived' && !result.isArchived) ||
        (state.filter !== 'archived' && result.isArchived);

      if (leftCurrentView) {
        excludedLocalThreadIdsRef.current.add(chatId);
      }

      if (leftCurrentView) {
        dispatch({ type: 'threads/remove', payload: chatId });
      } else {
        dispatch({
          type: 'threads/archive',
          payload: { chatId: result.chatId, isArchived: result.isArchived },
        });
      }

      await loadThreads();
    } catch {
      // ignore — list unchanged
    } finally {
      setIsTogglingArchive(false);
    }
  }, [activeId, activeThread, api, isTogglingArchive, loadThreads, state.filter]);

  const setGroupChatStatus = useCallback(
    async (chatId: number, nextStatus: 'active' | 'closed') => {
      if (isTogglingChatStatus) {
        return;
      }

      const thread =
        threadsRef.current.find((item) => item.id === chatId) ??
        (activeThread?.id === chatId ? activeThread : null);

      if (!thread || thread.kind !== 'group') {
        return;
      }

      setIsTogglingChatStatus(true);
      setCloseChatErrorMessage(null);

      try {
        await api.updateGroupChat(chatId, { status: nextStatus });

        dispatch({
          type: 'threads/upsert',
          payload: { ...thread, status: nextStatus },
        });

        setChatDetail((current) =>
          current?.id === chatId ? { ...current, status: nextStatus } : current,
        );

        if (nextStatus === 'closed') {
          setCloseChatTarget(null);
        }

        await loadThreads();
        await loadChatDetail(chatId);
      } catch (error) {
        const apiError = error as ChatApiError | undefined;
        const data =
          apiError &&
          typeof apiError === 'object' &&
          apiError.data &&
          typeof apiError.data === 'object'
            ? (apiError.data as Record<string, unknown>)
            : null;
        const message =
          (typeof data?.message === 'string' && data.message) || 'Failed to update chat status';

        if (nextStatus === 'closed') {
          setCloseChatErrorMessage(message);
        }
      } finally {
        setIsTogglingChatStatus(false);
      }
    },
    [activeThread, api, isTogglingChatStatus, loadChatDetail, loadThreads],
  );

  const openToggleChatStatusModal = useCallback(() => {
    if (!activeThread || !canDeleteActiveGroup || activeThread.kind !== 'group') {
      return;
    }

    if (activeThread.status === 'closed') {
      void setGroupChatStatus(activeThread.id, 'active');
      return;
    }

    setCloseChatErrorMessage(null);
    setCloseChatTarget({
      chatId: activeThread.id,
      title: activeThread.title,
    });
  }, [activeThread, canDeleteActiveGroup, setGroupChatStatus]);

  const closeCloseChatModal = useCallback(() => {
    if (isTogglingChatStatus) {
      return;
    }

    setCloseChatTarget(null);
    setCloseChatErrorMessage(null);
  }, [isTogglingChatStatus]);

  const confirmCloseChat = useCallback(async () => {
    if (!closeChatTarget) {
      return;
    }

    await setGroupChatStatus(closeChatTarget.chatId, 'closed');
  }, [closeChatTarget, setGroupChatStatus]);

  const setDraft = useCallback((value: string) => {
    dispatch({ type: 'draft/set', payload: value });
  }, []);

  const resetParticipantPicker = useCallback(() => {
    availableUsersRequestIdRef.current += 1;
    setAvailableUsersQuery('');
    setGroupParticipantsQuery('');
    setAvailableUsers([]);
    setIsAvailableUsersLoading(false);
  }, []);

  const openCreateChatModal = useCallback(() => {
    resetParticipantPicker();
    setIsCreateGroupChatModalOpen(false);
    setIsCreateChatModalOpen(true);
  }, [resetParticipantPicker]);

  const closeCreateChatModal = useCallback(() => {
    resetParticipantPicker();
    setIsCreateChatModalOpen(false);
    setIsCreatingPersonalChat(false);
  }, [resetParticipantPicker]);

  const openCreateGroupChatModal = useCallback(() => {
    resetParticipantPicker();
    setIsCreateChatModalOpen(false);
    setGroupTitle('');
    setGroupAvatarFile(null);
    setSelectedGroupParticipantIds([]);
    setCreateGroupErrorMessage(null);
    setIsCreatingGroupChat(false);
    setIsCreateGroupChatModalOpen(true);
  }, [resetParticipantPicker]);

  const closeCreateGroupChatModal = useCallback(() => {
    resetParticipantPicker();
    setIsCreateGroupChatModalOpen(false);
    setGroupTitle('');
    setGroupAvatarFile(null);
    setSelectedGroupParticipantIds([]);
    setCreateGroupErrorMessage(null);
    setIsCreatingGroupChat(false);
  }, [resetParticipantPicker]);

  const setGroupTitleValue = useCallback((value: string) => {
    setGroupTitle(value);
    setCreateGroupErrorMessage(null);
  }, []);

  const setGroupAvatarFileValue = useCallback((file: File | null) => {
    setGroupAvatarFile(file);
    setCreateGroupErrorMessage(null);
  }, []);

  const toggleGroupParticipant = useCallback((userId: number) => {
    setSelectedGroupParticipantIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      }

      if (prev.length >= CHAT_GROUP_MAX_PARTICIPANTS) {
        return prev;
      }

      return [...prev, userId];
    });
    setCreateGroupErrorMessage(null);
  }, []);

  const setGroupParticipantsSearch = useCallback((value: string) => {
    setGroupParticipantsQuery(value);
    setCreateGroupErrorMessage(null);
  }, []);

  const resetEditGroupChatState = useCallback(() => {
    setEditGroupSnapshot(null);
    setEditGroupTitle('');
    setEditGroupStatus('active');
    setEditGroupAvatarFile(null);
    setEditGroupAvatarPreviewUrl(null);
    setEditRemovedUserIds([]);
    setEditAddedUsers([]);
    setEditParticipantsQuery('');
    setUpdateGroupErrorMessage(null);
    setIsUpdatingGroupChat(false);
  }, []);

  const closeEditGroupChatModal = useCallback(() => {
    resetParticipantPicker();
    resetEditGroupChatState();
    setIsEditGroupChatModalOpen(false);
  }, [resetEditGroupChatState, resetParticipantPicker]);

  const openEditGroupChat = useCallback(() => {
    if (!chatDetail || !canManageStaffGroupChat(chatDetail, currentUserId, canManageGroups)) {
      return;
    }

    resetParticipantPicker();
    setEditGroupSnapshot(chatDetail);
    setEditGroupTitle(chatDetail.title);
    setEditGroupStatus(chatDetail.status);
    setEditGroupAvatarFile(null);
    setEditGroupAvatarPreviewUrl(chatDetail.avatarUrl);
    setEditRemovedUserIds([]);
    setEditAddedUsers([]);
    setEditParticipantsQuery('');
    setUpdateGroupErrorMessage(null);
    setChatDetailTarget(null);
    setChatDetailView('overview');
    setIsEditGroupChatModalOpen(true);
  }, [canManageGroups, chatDetail, currentUserId, resetParticipantPicker]);

  const setEditGroupTitleValue = useCallback((value: string) => {
    setEditGroupTitle(value);
    setUpdateGroupErrorMessage(null);
  }, []);

  const setEditGroupStatusValue = useCallback((value: 'active' | 'closed') => {
    setEditGroupStatus(value);
    setUpdateGroupErrorMessage(null);
  }, []);

  const setEditGroupAvatarFileValue = useCallback((file: File | null) => {
    setEditGroupAvatarFile(file);
    setUpdateGroupErrorMessage(null);
  }, []);

  const setEditParticipantsSearch = useCallback((value: string) => {
    setEditParticipantsQuery(value);
    setUpdateGroupErrorMessage(null);
  }, []);

  const removeEditGroupParticipant = useCallback(
    (userId: number) => {
      const participant = editGroupSnapshot?.participants.find((item) => item.user.id === userId);

      if (!participant || participant.user.id === currentUserId) {
        return;
      }

      if (participant.chatRole.toLowerCase() === 'owner') {
        return;
      }

      setEditRemovedUserIds((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
      setUpdateGroupErrorMessage(null);
    },
    [currentUserId, editGroupSnapshot?.participants],
  );

  const undoRemoveEditGroupParticipant = useCallback((userId: number) => {
    setEditRemovedUserIds((prev) => prev.filter((id) => id !== userId));
    setUpdateGroupErrorMessage(null);
  }, []);

  const toggleEditGroupAddedUser = useCallback(
    (user: ChatUser) => {
      setEditAddedUsers((prev) => {
        const exists = prev.some((item) => item.id === user.id);
        if (exists) {
          return prev.filter((item) => item.id !== user.id);
        }

        const currentActive =
          (editGroupSnapshot?.participants.filter(
            (participant) => !editRemovedUserIds.includes(participant.user.id),
          ).length ?? 0) + prev.length;

        if (currentActive >= CHAT_GROUP_MAX_PARTICIPANTS) {
          return prev;
        }

        return [...prev, user];
      });
      setUpdateGroupErrorMessage(null);
    },
    [editGroupSnapshot?.participants, editRemovedUserIds],
  );

  const createPersonalChat = useCallback(
    async (userId: number) => {
      setIsCreatingPersonalChat(true);

      try {
        const result = await api.createPersonalChat(userId);
        const chatId = resolvePersonalChatId(result);

        await loadThreads();
        dispatch({ type: 'active/set', payload: chatId });
        closeCreateChatModal();
      } catch (error) {
        const chatId = (() => {
          const apiError = error as ChatApiError | undefined;
          const envelope =
            apiError &&
            typeof apiError === 'object' &&
            apiError.data &&
            typeof apiError.data === 'object'
              ? (apiError.data as Record<string, unknown>)
              : null;
          const data =
            envelope?.data && typeof envelope.data === 'object'
              ? (envelope.data as Record<string, unknown>)
              : envelope;

          const id = data?.id ?? data?.chatId;
          return typeof id === 'number' ? id : null;
        })();

        if (chatId !== null) {
          await loadThreads();
          dispatch({ type: 'active/set', payload: chatId });
          closeCreateChatModal();
          return;
        }
      } finally {
        setIsCreatingPersonalChat(false);
      }
    },
    [api, closeCreateChatModal, loadThreads],
  );

  const createGroupChat = useCallback(async () => {
    const trimmedTitle = groupTitle.trim();

    if (!trimmedTitle || !groupAvatarFile || isCreatingGroupChat) {
      return;
    }

    setIsCreatingGroupChat(true);
    setCreateGroupErrorMessage(null);

    try {
      const result = await api.createGroupChat({
        title: trimmedTitle,
        avatarFile: groupAvatarFile,
        participantIds: selectedGroupParticipantIds.length
          ? selectedGroupParticipantIds
          : undefined,
      });

      await loadThreads();
      dispatch({ type: 'active/set', payload: result.id });
      closeCreateGroupChatModal();
    } catch {
      setCreateGroupErrorMessage('Failed to create group chat');
    } finally {
      setIsCreatingGroupChat(false);
    }
  }, [
    api,
    closeCreateGroupChatModal,
    groupAvatarFile,
    groupTitle,
    isCreatingGroupChat,
    loadThreads,
    selectedGroupParticipantIds,
  ]);

  const leaveChat = useCallback(
    async (chatId: number) => {
      if (isLeavingChat) {
        return;
      }

      setIsLeavingChat(true);
      setLeaveChatErrorMessage(null);

      try {
        await api.leaveChat(chatId);
        excludedLocalThreadIdsRef.current.add(chatId);
        dispatch({ type: 'threads/remove', payload: chatId });
        setLeaveChatTarget(null);
        closeChatDetails();
        closeEditGroupChatModal();
        await loadThreads();

        if (activeId === chatId) dispatch({ type: 'active/set', payload: null });
      } catch (error) {
        const apiError = error as ChatApiError | undefined;
        const data =
          apiError &&
          typeof apiError === 'object' &&
          apiError.data &&
          typeof apiError.data === 'object'
            ? (apiError.data as Record<string, unknown>)
            : null;
        const message =
          (typeof data?.message === 'string' && data.message) || 'Failed to leave chat';

        setLeaveChatErrorMessage(message);
      } finally {
        setIsLeavingChat(false);
      }
    },
    [activeId, api, closeChatDetails, closeEditGroupChatModal, isLeavingChat, loadThreads],
  );

  const openLeaveChatModal = useCallback(() => {
    if (!activeThread || !activeCanLeave) {
      return;
    }

    setLeaveChatErrorMessage(null);
    setLeaveChatTarget({
      chatId: activeThread.id,
      title: activeThread.title,
    });
  }, [activeCanLeave, activeThread]);

  const closeLeaveChatModal = useCallback(() => {
    if (isLeavingChat) {
      return;
    }

    setLeaveChatTarget(null);
    setLeaveChatErrorMessage(null);
  }, [isLeavingChat]);

  const confirmLeaveChat = useCallback(async () => {
    if (!leaveChatTarget) {
      return;
    }

    await leaveChat(leaveChatTarget.chatId);
  }, [leaveChat, leaveChatTarget]);

  const deleteChat = useCallback(
    async (chatId: number) => {
      if (isDeletingChat) {
        return;
      }

      setIsDeletingChat(true);
      setDeleteChatErrorMessage(null);

      try {
        await api.deleteChat(chatId);
        excludedLocalThreadIdsRef.current.add(chatId);
        dispatch({ type: 'threads/remove', payload: chatId });
        setDeleteChatTarget(null);
        closeChatDetails();
        closeEditGroupChatModal();
        await loadThreads();

        if (activeId === chatId) dispatch({ type: 'active/set', payload: null });
      } catch (error) {
        const apiError = error as ChatApiError | undefined;
        const data =
          apiError &&
          typeof apiError === 'object' &&
          apiError.data &&
          typeof apiError.data === 'object'
            ? (apiError.data as Record<string, unknown>)
            : null;
        const message =
          (typeof data?.message === 'string' && data.message) || 'Failed to delete chat';

        setDeleteChatErrorMessage(message);
      } finally {
        setIsDeletingChat(false);
      }
    },
    [activeId, api, closeChatDetails, closeEditGroupChatModal, isDeletingChat, loadThreads],
  );

  const openRemoveParticipantModal = useCallback(
    (userId: number) => {
      const chatId = chatDetail?.id ?? activeThread?.id;
      if (!chatId || !canManageActiveGroupParticipants || removingParticipantUserId !== null) {
        return;
      }

      const participant = chatDetail?.participants.find((item) => item.user.id === userId);
      const participantName = participant
        ? [participant.user.firstName, participant.user.lastName]
            .filter(Boolean)
            .join(' ')
            .trim() || participant.user.username
        : 'this participant';

      setRemoveParticipantErrorMessage(null);
      setRemoveParticipantTarget({
        chatId,
        userId,
        participantName,
      });
    },
    [
      activeThread?.id,
      canManageActiveGroupParticipants,
      chatDetail?.id,
      chatDetail?.participants,
      removingParticipantUserId,
    ],
  );

  const closeRemoveParticipantModal = useCallback(() => {
    if (removingParticipantUserId !== null) {
      return;
    }

    setRemoveParticipantTarget(null);
    setRemoveParticipantErrorMessage(null);
  }, [removingParticipantUserId]);

  const confirmRemoveGroupParticipant = useCallback(async () => {
    if (!removeParticipantTarget || removingParticipantUserId !== null) {
      return;
    }

    const { chatId, userId } = removeParticipantTarget;

    setRemovingParticipantUserId(userId);
    setRemoveParticipantErrorMessage(null);
    setAdminToggleErrorMessage(null);

    try {
      await api.updateGroupChat(chatId, { removeParticipantIds: [userId] });
      await loadChatDetail(chatId);
      await loadThreads();
      setRemoveParticipantTarget(null);
    } catch (error) {
      const apiError = error as ChatApiError | undefined;
      const data =
        apiError &&
        typeof apiError === 'object' &&
        apiError.data &&
        typeof apiError.data === 'object'
          ? (apiError.data as Record<string, unknown>)
          : null;
      const message =
        (typeof data?.message === 'string' && data.message) || 'Failed to remove participant';

      setRemoveParticipantErrorMessage(message);
    } finally {
      setRemovingParticipantUserId(null);
    }
  }, [api, loadChatDetail, loadThreads, removeParticipantTarget, removingParticipantUserId]);

  const toggleParticipantAdmin = useCallback(
    async (userId: number) => {
      const chatId = chatDetail?.id ?? activeThread?.id;
      if (!chatId || !canManageActiveGroupParticipants || togglingAdminUserId !== null) {
        return;
      }

      setTogglingAdminUserId(userId);
      setAdminToggleErrorMessage(null);

      try {
        await api.toggleGroupAdmin(chatId, [userId]);
        await loadChatDetail(chatId);
      } catch (error) {
        const apiError = error as ChatApiError | undefined;
        const data =
          apiError &&
          typeof apiError === 'object' &&
          apiError.data &&
          typeof apiError.data === 'object'
            ? (apiError.data as Record<string, unknown>)
            : null;
        const message =
          (typeof data?.message === 'string' && data.message) || 'Failed to update admin role';

        setAdminToggleErrorMessage(message);
      } finally {
        setTogglingAdminUserId(null);
      }
    },
    [
      activeThread?.id,
      api,
      canManageActiveGroupParticipants,
      chatDetail?.id,
      loadChatDetail,
      togglingAdminUserId,
    ],
  );

  const openDeleteChatModal = useCallback(() => {
    if (!activeThread || !canDeleteActiveGroup) {
      return;
    }

    setDeleteChatErrorMessage(null);
    setDeleteChatTarget({
      chatId: activeThread.id,
      title: activeThread.title,
    });
  }, [activeThread, canDeleteActiveGroup]);

  const closeDeleteChatModal = useCallback(() => {
    if (isDeletingChat) {
      return;
    }

    setDeleteChatTarget(null);
    setDeleteChatErrorMessage(null);
  }, [isDeletingChat]);

  const confirmDeleteChat = useCallback(async () => {
    if (!deleteChatTarget) {
      return;
    }

    await deleteChat(deleteChatTarget.chatId);
  }, [deleteChat, deleteChatTarget]);

  const updateGroupChat = useCallback(async () => {
    if (!editGroupSnapshot || !canSaveGroupEdit || isUpdatingGroupChat) {
      return;
    }

    const payload: {
      title?: string;
      status?: 'active' | 'closed';
      avatarFile?: File;
      addParticipantIds?: number[];
      removeParticipantIds?: number[];
    } = {};

    const trimmedTitle = editGroupTitle.trim();

    if (trimmedTitle !== editGroupSnapshot.title) {
      payload.title = trimmedTitle;
    }

    if (editGroupStatus !== editGroupSnapshot.status) {
      payload.status = editGroupStatus;
    }

    if (editGroupAvatarFile) {
      payload.avatarFile = editGroupAvatarFile;
    }

    if (editAddedUsers.length) {
      payload.addParticipantIds = editAddedUsers.map((user) => user.id);
    }

    if (editRemovedUserIds.length) {
      payload.removeParticipantIds = editRemovedUserIds;
    }

    setIsUpdatingGroupChat(true);
    setUpdateGroupErrorMessage(null);

    try {
      await api.updateGroupChat(editGroupSnapshot.id, payload);
      await loadThreads();
      await loadChatDetail(editGroupSnapshot.id);
      dispatch({ type: 'active/set', payload: editGroupSnapshot.id });
      closeEditGroupChatModal();
    } catch (error) {
      const apiError = error as ChatApiError | undefined;
      const data =
        apiError &&
        typeof apiError === 'object' &&
        apiError.data &&
        typeof apiError.data === 'object'
          ? (apiError.data as Record<string, unknown>)
          : null;
      const message =
        (typeof data?.message === 'string' && data.message) || 'Failed to update group chat';

      setUpdateGroupErrorMessage(message);
    } finally {
      setIsUpdatingGroupChat(false);
    }
  }, [
    api,
    canSaveGroupEdit,
    closeEditGroupChatModal,
    editAddedUsers,
    editGroupAvatarFile,
    editGroupSnapshot,
    editGroupStatus,
    editGroupTitle,
    editRemovedUserIds,
    isUpdatingGroupChat,
    loadChatDetail,
    loadThreads,
  ]);

  return {
    filter: state.filter,
    setFilter,
    threadsSearch,
    setThreadsSearch,
    isThreadsLoading: state.isThreadsLoading,
    isThreadsLoadingMore,
    threadsHasMore,
    loadMoreThreads,
    activeId,
    activeThread,
    activeGroupParticipantsCount,
    filteredThreads,
    messages,
    hasOlderMessages,
    isLoadingOlderMessages,
    loadOlderMessages,
    draft: state.draft,
    draftAttachments,
    replyToMessage,
    pinnedMessages,
    composerFocusRequestId,
    chatDetail,
    chatDetailTarget,
    chatDetailView,
    isChatDetailLoading,
    chatDetailErrorMessage,
    ensureActiveChatDetailLoaded,
    chatReportTarget,
    chatReportReason,
    chatReportComment,
    isSubmittingChatReport,
    chatReportErrorMessage,
    isReportModalOpen: !!reportTargetMessage,
    isReportSuccessModalOpen,
    isChatDetailsModalOpen: !!chatDetailTarget,
    isChatReportModalOpen: !!chatReportTarget,
    isForwardModalOpen: !!forwardTargetMessage,
    forwardTargetMessage,
    forwardableThreads,
    forwardQuery,
    selectedForwardChatIds,
    isForwarding,
    forwardErrorMessage,
    reportTargetMessage,
    reportReason,
    reportComment,
    isSubmittingReport,
    reportErrorMessage,
    setDraft,
    setForwardQuery: updateForwardQuery,
    setReportReason: updateReportReason,
    setReportComment: updateReportComment,
    setChatReportReason: updateChatReportReason,
    setChatReportComment: updateChatReportComment,
    send,
    attachFiles,
    removeDraftAttachment,
    retryMessage,
    startReply,
    clearReply,
    canPinMessages,
    togglePinnedMessage,
    unpinPinnedMessage,
    isPinningMessage,
    openForwardMessage,
    closeForwardModal,
    toggleForwardChat,
    submitForward,
    openReportMessage,
    closeReportModal,
    closeReportSuccessModal,
    submitReport,
    openChatDetails,
    closeChatDetails,
    openChatDetailView,
    goBackFromChatDetailView,
    openChatReportModal,
    closeChatReportModal,
    submitChatReport,
    selectChat,
    isCreateChatModalOpen,
    openCreateChatModal,
    closeCreateChatModal,
    isCreateGroupChatModalOpen,
    openCreateGroupChatModal,
    closeCreateGroupChatModal,
    availableUsersQuery,
    setAvailableUsersQuery,
    availableUsers,
    isAvailableUsersLoading,
    isCreatingPersonalChat,
    createPersonalChat,
    groupTitle,
    setGroupTitle: setGroupTitleValue,
    groupAvatarFile,
    groupAvatarPreviewUrl,
    setGroupAvatarFile: setGroupAvatarFileValue,
    groupParticipantsQuery,
    setGroupParticipantsQuery: setGroupParticipantsSearch,
    selectedGroupParticipantIds,
    toggleGroupParticipant,
    isCreatingGroupChat,
    createGroupErrorMessage,
    createGroupChat,
    canManageActiveGroup,
    canManageActiveGroupParticipants,
    openEditGroupChat,
    isEditGroupChatModalOpen,
    closeEditGroupChatModal,
    editGroupSnapshot,
    editGroupTitle,
    setEditGroupTitle: setEditGroupTitleValue,
    editGroupStatus,
    setEditGroupStatus: setEditGroupStatusValue,
    editGroupAvatarFile,
    editGroupAvatarPreviewUrl,
    setEditGroupAvatarFile: setEditGroupAvatarFileValue,
    editRemovedUserIds,
    editAddedUsers,
    removeEditGroupParticipant,
    undoRemoveEditGroupParticipant,
    toggleEditGroupAddedUser,
    editParticipantsQuery,
    setEditParticipantsQuery: setEditParticipantsSearch,
    isUpdatingGroupChat,
    updateGroupErrorMessage,
    canSaveGroupEdit,
    updateGroupChat,
    activeCanLeave,
    canSendMessages,
    composerPlaceholder,
    isLeavingChat,
    isLeaveChatModalOpen: !!leaveChatTarget,
    leaveChatModalTitle: leaveChatTarget?.title ?? '',
    leaveChatErrorMessage,
    openLeaveChatModal,
    closeLeaveChatModal,
    confirmLeaveChat,
    leaveChat,
    canDeleteActiveGroup,
    isDeletingChat,
    isDeleteChatModalOpen: !!deleteChatTarget,
    deleteChatModalTitle: deleteChatTarget?.title ?? '',
    deleteChatErrorMessage,
    openDeleteChatModal,
    closeDeleteChatModal,
    confirmDeleteChat,
    isTogglingChatStatus,
    isCloseChatModalOpen: !!closeChatTarget,
    closeChatModalTitle: closeChatTarget?.title ?? '',
    closeChatErrorMessage,
    openToggleChatStatusModal,
    closeCloseChatModal,
    confirmCloseChat,
    deleteChat,
    canToggleGroupAdmin,
    togglingAdminUserId,
    adminToggleErrorMessage,
    toggleParticipantAdmin,
    openRemoveParticipantModal,
    isRemoveParticipantModalOpen: !!removeParticipantTarget,
    removeParticipantModalName: removeParticipantTarget?.participantName ?? '',
    removeParticipantErrorMessage,
    closeRemoveParticipantModal,
    confirmRemoveGroupParticipant,
    removingParticipantUserId,
    isTogglingArchive,
    toggleActiveChatArchive,
  };
}
